import { normalizeAdvertiserItem } from '@/lib/adapters/advertiser.adapter'
import { normalizeAdvertisersCategories } from '@/lib/adapters/advertisers.adapter'
import { normalizeMoney } from '@/lib/adapters/common.adapter'
import { normalizeImage } from '@/lib/adapters/image.adapter'
import { formatLocalDateToUtcWithoutOffset } from '@/lib/helpers'
import type { AffiliateLinkType } from '@/lib/types/affiliate-link.types'
import type {
  ReturnAdFormType,
  ReturnAdPayloadType,
  ReturnAdPreviewType,
  ReturnAdServerType,
} from '@/lib/types/return-ad.types'

export const normalizeReturnAd = (ad: ReturnAdServerType): ReturnAdFormType => {
  return {
    id: ad.id,
    internalName: ad.name,
    advertiserGroup: ad.advertiser_group_id,
    returnLink: ad.affiliate_link.original_url,
    advertiser: normalizeAdvertiserItem(ad.affiliate_link.advertiser),
    advertiserList: ad.advertisers
      ? ad.advertisers.map((item) => ({ id: item.id, label: item.name }))
      : [],
    categories: ad.advertiser_categories
      ? normalizeAdvertisersCategories(ad.advertiser_categories)
      : [],
    isAllAdvertisersSelected: ad.display_for_all_advertisers,
    image: ad.image ? normalizeImage(ad.image) : undefined,
    styles: ad.styles,
    startsAt: ad.starts_at,
    endsAt: ad.ends_at,
    hasStartsNow: !ad.starts_at,
    hasOpenEnd: !ad.ends_at && !ad.click_limit && !ad.view_limit,
    status: ad.status,
    clickLimit: ad.click_limit || 0,
    viewLimit: ad.view_limit || 0,
    stats: {
      orderCount: ad.stats.order_count,
      orderValue: normalizeMoney(ad.stats.order_value),
      earnings: normalizeMoney(ad.stats.earnings),
      currentClicks: ad.stats.current_clicks,
      currentViews: ad.stats.current_views,
      clickThroughRate: ad.stats.click_through_rate,
    },
  }
}

export const serializeReturnAd = (ad: ReturnAdFormType): ReturnAdPayloadType => {
  if (!ad.advertiser) {
    throw new Error('Affiliate link is required')
  }

  return {
    name: ad.internalName,
    advertiser_group_id: ad.advertiserGroup,
    return_link: ad.returnLink,
    styles: {
      text: ad.styles.text,
      backgroundType: ad.styles.backgroundType,
      backgroundColor: ad.styles.backgroundColor,
      textColor: ad.styles.textColor,
      textPosition: ad.styles.textPosition,
      buttonBackgroundColor: ad.styles.buttonBackgroundColor,
      buttonTextColor: ad.styles.buttonTextColor,
    },
    display_for_all_advertisers: ad.isAllAdvertisersSelected,
    advertiser_id: ad.advertiser.id,
    advertiser_ids: !ad.isAllAdvertisersSelected ? ad.advertiserList.map((item) => item.id) : [],
    advertiser_category_ids: !ad.isAllAdvertisersSelected
      ? ad.categories.map((item) => item.id)
      : [],
    ...(ad.styles.backgroundType === 'solid'
      ? { image_key: null }
      : ad.tempImage
        ? { image_key: ad.tempImage.key }
        : {}),
    starts_at:
      !ad.hasStartsNow && ad.startsAt ? formatLocalDateToUtcWithoutOffset(ad.startsAt) : null,
    ends_at: (!ad.hasOpenEnd && ad.endsAt) || null,
    click_limit: !ad.hasOpenEnd && ad.clickLimit ? ad.clickLimit : null,
    view_limit: !ad.hasOpenEnd && ad.viewLimit ? ad.viewLimit : null,
  }
}

export const normalizeReturnAdFormToPreview = (data: ReturnAdFormType): ReturnAdPreviewType => {
  return {
    id: data.id || 0,
    affiliateLink: {
      advertiser: data.advertiser!,
      url: data.returnLink,
    } as AffiliateLinkType,
    styles: data.styles,
    image: data.tempImage || data.image,
  }
}
